import {
  FLOW_INPUT_COMPONENT_TYPES,
  LATEST_FLOW_JSON_VERSION,
  getFlowComponent,
} from '@leadsync/shared-types';

/**
 * Editor model for the visual Flow builder, plus the parse/serialize bridge to
 * Meta Flow JSON. `flowJson` stays the source of truth — the builder parses it
 * into this model, the user edits the model, and it serialises straight back.
 *
 * Deliberately scoped: the builder represents the COMMON shape (one Form per
 * screen, a recognised component set, complete/navigate footers). Anything
 * outside that — If/Switch, NavigationList, data_exchange actions, multiple
 * Forms — makes `parseFlowToModel` return null and the editor falls back to the
 * raw JSON view. Pure module (no React) so it unit-tests without a DOM.
 */

/** Component types the visual builder can render + edit. Others → JSON only. */
export const BUILDER_SUPPORTED_TYPES: ReadonlySet<string> = new Set([
  'TextHeading',
  'TextSubheading',
  'TextBody',
  'TextCaption',
  'RichText',
  'Image',
  'TextInput',
  'TextArea',
  'RadioButtonsGroup',
  'CheckboxGroup',
  'Dropdown',
  'ChipsSelector',
  'DatePicker',
  'OptIn',
  'PhotoPicker',
  'DocumentPicker',
]);

export interface BuilderComponent {
  /** Flow JSON `type`. */
  type: string;
  /** All Flow JSON keys except `type` (kebab-case), e.g. name, label, data-source. */
  props: Record<string, unknown>;
}

export interface BuilderFooter {
  label: string;
  action: 'complete' | 'navigate';
  /** Target screen id when action === 'navigate'. */
  next?: string;
}

export interface BuilderScreen {
  id: string;
  title: string;
  terminal: boolean;
  components: BuilderComponent[];
  footer: BuilderFooter | null;
  /** Preserved screen `data` model (navigate carry-over). */
  data?: Record<string, unknown>;
  /** Footer payload entries that are NOT `${form.*}` (e.g. `${data.*}`) — kept verbatim. */
  preservedPayload?: Record<string, unknown>;
}

export interface BuilderModel {
  version: string;
  screens: BuilderScreen[];
}

const FORM_REF = /^\$\{form\./;

function isInput(type: string): boolean {
  return FLOW_INPUT_COMPONENT_TYPES.has(type);
}

/** Parse Flow JSON into the editor model, or null if too advanced to represent. */
export function parseFlowToModel(flowJson: string): BuilderModel | null {
  let doc: unknown;
  try {
    doc = JSON.parse(flowJson);
  } catch {
    return null;
  }
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;
  const root = doc as Record<string, unknown>;
  const version = typeof root.version === 'string' ? root.version : null;
  if (!version) return null;
  if (!Array.isArray(root.screens) || root.screens.length === 0) return null;

  const screens: BuilderScreen[] = [];
  for (const raw of root.screens) {
    const screen = parseScreen(raw);
    if (!screen) return null;
    screens.push(screen);
  }
  return { version, screens };
}

function parseScreen(raw: unknown): BuilderScreen | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.id !== 'string' || s.id.trim() === '') return null;

  const layout = s.layout as Record<string, unknown> | undefined;
  const children = layout?.children;
  if (!Array.isArray(children)) return null;

  const components: BuilderComponent[] = [];
  let footer: BuilderFooter | null = null;
  let preservedPayload: Record<string, unknown> | undefined;
  let formsSeen = 0;

  const consume = (child: unknown): boolean => {
    if (!child || typeof child !== 'object') return false;
    const c = child as Record<string, unknown>;
    const type = c.type;
    if (typeof type !== 'string') return false;

    if (type === 'Footer') {
      const parsed = parseFooter(c);
      if (!parsed) return false;
      footer = parsed.footer;
      preservedPayload = parsed.preservedPayload;
      return true;
    }
    if (!BUILDER_SUPPORTED_TYPES.has(type)) return false; // If/Switch/NavigationList/unknown
    const { type: _t, ...props } = c;
    components.push({ type, props });
    return true;
  };

  for (const child of children) {
    const c = child as Record<string, unknown>;
    if (c?.type === 'Form') {
      formsSeen += 1;
      if (formsSeen > 1) return null; // multiple Forms — too complex for the builder
      const formChildren = c.children;
      if (!Array.isArray(formChildren)) return null;
      for (const fc of formChildren) {
        if (!consume(fc)) return null;
      }
    } else {
      if (!consume(child)) return null;
    }
  }

  const out: BuilderScreen = {
    id: s.id,
    title: typeof s.title === 'string' ? s.title : '',
    terminal: s.terminal === true,
    components,
    footer,
  };
  if (s.data && typeof s.data === 'object') out.data = s.data as Record<string, unknown>;
  if (preservedPayload && Object.keys(preservedPayload).length > 0)
    out.preservedPayload = preservedPayload;
  return out;
}

function parseFooter(
  c: Record<string, unknown>,
): { footer: BuilderFooter; preservedPayload: Record<string, unknown> } | null {
  const action = c['on-click-action'] as Record<string, unknown> | undefined;
  const name = action?.name;
  if (name !== 'complete' && name !== 'navigate') return null; // data_exchange etc. → JSON only

  const footer: BuilderFooter = {
    label: typeof c.label === 'string' ? c.label : 'Submit',
    action: name,
  };
  if (name === 'navigate') {
    const next = action?.next;
    if (typeof next === 'string') footer.next = next;
    else if (next && typeof next === 'object') {
      const n = (next as Record<string, unknown>).name;
      if (typeof n === 'string') footer.next = n;
    }
  }

  const preservedPayload: Record<string, unknown> = {};
  const payload = action?.payload;
  if (payload && typeof payload === 'object') {
    for (const [k, v] of Object.entries(payload as Record<string, unknown>)) {
      if (typeof v !== 'string' || !FORM_REF.test(v)) preservedPayload[k] = v;
    }
  }
  return { footer, preservedPayload };
}

/** Serialise the editor model back into Meta Flow JSON (pretty-printed). */
export function serializeModelToFlow(model: BuilderModel): string {
  const screens = model.screens.map((screen) => {
    const firstInputIdx = screen.components.findIndex((c) => isInput(c.type));
    const leading =
      firstInputIdx === -1 ? screen.components : screen.components.slice(0, firstInputIdx);
    const inForm = firstInputIdx === -1 ? [] : screen.components.slice(firstInputIdx);

    const footerJson = screen.footer ? buildFooterJson(screen) : null;

    const layoutChildren: Array<Record<string, unknown>> = leading.map(toComponentJson);
    if (inForm.length > 0) {
      const formChildren = inForm.map(toComponentJson);
      if (footerJson) formChildren.push(footerJson);
      layoutChildren.push({ type: 'Form', name: 'form', children: formChildren });
    } else if (footerJson) {
      layoutChildren.push(footerJson);
    }

    const screenJson: Record<string, unknown> = {
      id: screen.id,
      title: screen.title,
      terminal: screen.terminal,
    };
    if (screen.data) screenJson.data = screen.data;
    screenJson.layout = { type: 'SingleColumnLayout', children: layoutChildren };
    return screenJson;
  });

  return JSON.stringify({ version: model.version, screens }, null, 2);
}

function toComponentJson(c: BuilderComponent): Record<string, unknown> {
  return { type: c.type, ...c.props };
}

function buildFooterJson(screen: BuilderScreen): Record<string, unknown> {
  const footer = screen.footer!;
  // Payload: keep non-${form.*} carry-overs, then (re)generate a ${form.name}
  // entry for every input on the screen so it tracks the current fields.
  const payload: Record<string, unknown> = { ...(screen.preservedPayload ?? {}) };
  for (const comp of screen.components) {
    if (isInput(comp.type)) {
      const name = comp.props.name;
      if (typeof name === 'string' && name.trim() !== '') {
        payload[name] = `\${form.${name}}`;
      }
    }
  }
  const action: Record<string, unknown> = { name: footer.action, payload };
  if (footer.action === 'navigate' && footer.next) {
    action.next = { type: 'screen', name: footer.next };
  }
  return { type: 'Footer', label: footer.label, 'on-click-action': action };
}

/** True when the builder can represent this Flow JSON (else use the JSON view). */
export function canRepresentInBuilder(flowJson: string): boolean {
  return parseFlowToModel(flowJson) !== null;
}

let fieldCounter = 0;
/** Generate a fresh, JSON-safe field name like `field_3`. */
function nextFieldName(existing: Set<string>): string {
  let name = '';
  do {
    fieldCounter += 1;
    name = `field_${fieldCounter}`;
  } while (existing.has(name));
  return name;
}

/** Default props for a freshly-added component of the given type. */
export function blankComponent(type: string, existingNames: Set<string>): BuilderComponent {
  const desc = getFlowComponent(type);
  const props: Record<string, unknown> = {};
  if (desc?.capturesInput) {
    props.name = nextFieldName(existingNames);
    props.label = desc.label;
  }
  switch (type) {
    case 'TextHeading':
    case 'TextSubheading':
      props.text = 'Heading';
      break;
    case 'TextBody':
    case 'TextCaption':
    case 'RichText':
      props.text = 'Text';
      break;
    case 'Image':
      props.src = '';
      break;
    case 'TextInput':
      props['input-type'] = 'text';
      props.required = true;
      break;
    case 'TextArea':
      props.required = false;
      break;
    case 'RadioButtonsGroup':
    case 'CheckboxGroup':
    case 'Dropdown':
    case 'ChipsSelector':
      props['data-source'] = [
        { id: 'option_1', title: 'Option 1' },
        { id: 'option_2', title: 'Option 2' },
      ];
      if (type !== 'ChipsSelector') props.required = true;
      break;
    case 'DatePicker':
      props.required = true;
      break;
    case 'OptIn':
      props.required = true;
      break;
    case 'PhotoPicker':
      props['min-uploaded-photos'] = 1;
      props['max-uploaded-photos'] = 5;
      break;
    case 'DocumentPicker':
      props['min-uploaded-documents'] = 1;
      props['max-uploaded-documents'] = 3;
      break;
    default:
      break;
  }
  return { type, props };
}

/** A fresh single-screen model for a brand-new / unrepresentable flow. */
export function blankScreen(id: string): BuilderScreen {
  return {
    id,
    title: 'Screen',
    terminal: true,
    components: [
      { type: 'TextHeading', props: { text: 'Your response' } },
      { type: 'TextInput', props: { name: 'response', label: 'Your answer', required: true } },
    ],
    footer: { label: 'Submit', action: 'complete' },
  };
}

/** Collect every input `name` already used across the model (for uniqueness). */
export function collectNames(model: BuilderModel): Set<string> {
  const names = new Set<string>();
  for (const screen of model.screens) {
    for (const c of screen.components) {
      if (isInput(c.type) && typeof c.props.name === 'string') names.add(c.props.name);
    }
  }
  return names;
}

export { LATEST_FLOW_JSON_VERSION };
